import React, { Fragment } from "react";
import {
  Row,
  Col,
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge
} from "reactstrap";
import moment from "moment";
import parse, { domToReact } from "html-react-parser";
import _ from "lodash";
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  WhatsappIcon
} from "react-share";
import PostulationForm from "../postulation/Form";
import PublicationQuestions from "./Questions";

const PRIVILEGED_ROLES = ["admin", "recruiter", "job_head"];

// Helper function to safely parse HTML and convert style attributes to React style objects
// This prevents React from interpreting CSS font-family values as props
const safeParse = (html) => {
  if (!html) return null;

  // List of known problematic attribute names that are incorrectly parsed font-family values
  const problematicAttributes = new Set([
    'Segoe', 'UI', 'Roboto', 'Helvetica', 'Neue', 'Fira', 'Sans', 'Ubuntu', 'Oxygen',
    'Cantarell', 'Droid', 'Apple', 'Emoji', 'Symbol', 'Lucida', 'Grande', 'Arial',
    'uI', 'uI",', 'roboto,', '"Helvetica', 'neue",', '"Fira', 'sans",', 'ubuntu,',
    'oxygen,', '"Oxygen', 'cantarell,', '"Droid', '"Apple', 'emoji",', '"Segoe',
    'symbol",', '"Lucida', 'grande",', 'helvetica,', 'arial,', 'sans-serif;',
    'Color' // Also handle Color -> color
  ]);

  // Use html-react-parser with a replace function that filters problematic attributes
  const replaceFunction = (domNode) => {
    if (domNode.type === 'tag' && domNode.attribs) {
      const attribs = { ...domNode.attribs };
      let styleObj = null;

      // Process style attribute if present
      if (attribs.style) {
        const styleString = typeof attribs.style === 'string' ? attribs.style : String(attribs.style);
        styleObj = {};

        // Parse CSS style string into object
        styleString.split(';').forEach(declaration => {
          const trimmed = declaration.trim();
          if (trimmed) {
            const colonIndex = trimmed.indexOf(':');
            if (colonIndex > 0) {
              const property = trimmed.substring(0, colonIndex).trim();
              const value = trimmed.substring(colonIndex + 1).trim();
              if (property && value) {
                // Convert kebab-case to camelCase (e.g., font-family -> fontFamily)
                const camelProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                styleObj[camelProperty] = value;
              }
            }
          }
        });

        // Remove style from attribs - we'll add it back as an object
        delete attribs.style;
      }

      // Clean up any problematic attributes that might have been incorrectly parsed
      const cleanedAttribs = {};

      Object.keys(attribs).forEach(key => {
        // Skip problematic attributes (incorrectly parsed font-family values)
        if (problematicAttributes.has(key)) {
          return;
        }

        // Skip attributes with invalid names (contain quotes, commas, semicolons, etc.)
        if (key.includes('"') || key.includes("'") || key.includes(',') || key.includes(';')) {
          return;
        }

        // Skip attributes that don't start with a letter or underscore
        if (!/^[a-zA-Z_]/.test(key)) {
          return;
        }

        // Convert first letter to lowercase if it's uppercase (e.g., Color -> color)
        let cleanedKey = key.charAt(0).toLowerCase() + key.slice(1);

        // Only keep valid HTML attribute names
        // Allow data-* and aria-* attributes, and standard HTML attributes
        const isValidAttribute = cleanedKey.startsWith('data-') ||
                                 cleanedKey.startsWith('aria-') ||
                                 /^[a-z][a-zA-Z0-9_-]*$/.test(cleanedKey);

        if (isValidAttribute) {
          cleanedAttribs[cleanedKey] = attribs[key];
        }
      });

      // Add style object if we have one
      if (styleObj && Object.keys(styleObj).length > 0) {
        cleanedAttribs.style = styleObj;
      }

      // Process children recursively
      const children = domNode.children && domNode.children.length > 0
        ? domToReact(domNode.children, { replace: replaceFunction })
        : undefined;

      return React.createElement(
        domNode.name,
        {
          ...cleanedAttribs,
          key: Math.random()
        },
        children
      );
    }
    // Return undefined to use default parsing for non-tag nodes
    return undefined;
  };

  return parse(html, {
    replace: replaceFunction
  });
};

const PublicationShow = props => {
  const {
    postulationAnswers,
    isAlreadyPostulated,
    user,
    course,
    isPreview,
    userSignedIn,
    role,
    currentUserRole
  } = props;
  const { minisitePublication } = course;

  const description = isPreview
    ? safeParse(course.jobDescription.value)
    : safeParse(course.jobDescription);

  const benefits = isPreview
    ? safeParse(minisitePublication.benefits.value)
    : safeParse(minisitePublication.benefits);

  const isConfidential = isPreview
    ? minisitePublication.isConfidential.value
    : minisitePublication.isConfidential;

  const truncateText = (str, len) => {
    if (str.length > len) {
      return str.substring(0, len) + "...";
    }
  };

  const hasPrivilegedRole = Array.isArray(currentUserRole)
    ? currentUserRole.some(r => PRIVILEGED_ROLES.includes(r))
    : PRIVILEGED_ROLES.includes(currentUserRole);

  const isPrivileged = hasPrivilegedRole;

  const desactivateAt = isPreview
    ? moment().format("YYYY-MM-DD")
    : minisitePublication?.desactivateAt;

  const isExpired = desactivateAt
    ? moment().isAfter(moment(desactivateAt).endOf("day"))
    : false;

  const profilePositionFileUrl = minisitePublication.profilePositionFileUrl;

  return (
    <div className="publication-layout">
      <div className="bg-job bg-img d-flex align-items-center"></div>
      <Card className="mb-4 publication-full-card border-0 rounded-4" id="publication_show_info">
        <CardHeader className="p-4 shadow-sm border-bottom-0">
          <Row>
            <Col lg="9" md="8">
              <h2 className="fs-4 fw-bold mb-3" id="publication_title">
                {isPreview ? course.title.value : course.title}&nbsp;
                {/*{publication.isPublic && publication.is_new? && (
                  <Badge color="primary" className="me-2">
                    Nueva Oferta
                  </Badge>
                )}*/}
              </h2>
              <div className="media mb-4 mb-md-0 d-flex align-items-start flex-wrap">
                <div className="media-body">
                  <ul className="fa-ul list-unstyled mb-0 ms-4 publication-detail-list" id="publication-detail-list">
                    <li>
                      <span className="fa-li">
                        <i
                          aria-hidden="true"
                          className="far fa-calendar-alt"
                          title="Fecha de Publicación"
                        ></i>
                      </span>
                      {isPreview
                        ? moment().format("DD/MM/YYYY")
                        : moment(course.createdAt).format("DD/MM/YYYY")}
                    </li>
                    <li>
                      <span className="fa-li">
                        <i
                          aria-hidden="true"
                          className="fas fa-briefcase"
                          title="Contrato"
                        ></i>
                      </span>
                      {course.contractType.label}
                    </li>
                    <li>
                      <span className="fa-li">
                        <i
                          aria-hidden="true"
                          className="fas fa-business-time"
                          title="Jornada, Horarios y/o Turnos"
                        ></i>
                      </span>
                      {!isPreview
                        ? `${
                          minisitePublication.preferredSchedule
                            ? I18n.t(
                              "activerecord.enums.personal/laboral_requirement.preferred_schedules." +
                              minisitePublication.preferredSchedule
                            )
                            : ""
                        }. ${
                          minisitePublication.preferredLaborDay
                            ? I18n.t(
                              "activerecord.enums.personal/laboral_requirement.preferred_labor_days." +
                              minisitePublication.preferredLaborDay
                            )
                            : ""
                        }. ${
                          minisitePublication.preferredTurn
                            ? I18n.t(
                              "activerecord.enums.personal/laboral_requirement.preferred_turns." +
                              minisitePublication.preferredTurn
                            )
                            : ""
                        }`
                        : `${
                          minisitePublication.preferredSchedule.value
                            ? I18n.t(
                              "activerecord.enums.personal/laboral_requirement.preferred_schedules." +
                              minisitePublication.preferredSchedule.value
                            )
                            : ""
                        }. ${
                          minisitePublication.preferredLaborDay.value
                            ? I18n.t(
                              "activerecord.enums.personal/laboral_requirement.preferred_labor_days." +
                              minisitePublication.preferredLaborDay.value
                            )
                            : ""
                        }. ${
                          minisitePublication.preferredTurn.value
                            ? I18n.t(
                              "activerecord.enums.personal/laboral_requirement.preferred_turns." +
                              minisitePublication.preferredTurn.value
                            )
                            : ""
                        }.`}
                    </li>
                    {minisitePublication.dressCode && (
                      <li>
                        <span className="fa-li">
                          <i
                            aria-hidden="true"
                            className="fas fa-user-tie"
                          ></i>
                        </span>

                        {!isPreview
                          ? `${
                            minisitePublication.dressCode
                              ? I18n.t(
                                "activerecord.enums.personal/laboral_requirement.dress_code." +
                                minisitePublication.dressCode
                              )
                              : ""
                          }`
                          : `${
                            minisitePublication.dressCode.value
                              ? I18n.t(
                                "activerecord.enums.personal/laboral_requirement.dress_code." +
                                minisitePublication.dressCode.value
                              )
                              : ""
                          }`}
                      </li>
                    )}
                    <li>
                      <span className="fa-li">
                        <i
                          aria-hidden="true"
                          className="fas fa-users"
                          title="Vacantes"
                        ></i>
                      </span>
                      {isPreview ? course.vacancies.value : course.vacancies}{" "}
                      Vacantes
                    </li>
                    {!isConfidential && (
                      <li>
                        <span className="fa-li">
                          <i
                            aria-hidden="true"
                            className="fas fa-map-marker-alt"
                            title="Ubicación"
                          ></i>
                        </span>
                        {!isPreview
                          ? `${minisitePublication.address}. ${minisitePublication.commune.name}, ${minisitePublication.region.name}, ${minisitePublication.country.name}`
                          : `${minisitePublication.address.value}. ${minisitePublication.commune.label}, ${minisitePublication.region.label}, ${minisitePublication.country.label}`}
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </Col>
            <Col lg="3" md="4" className="text-right text-end rs_skip_always">
              {/*Social Buttons*/}
              {!isPreview && (
                <div className="d-flex justify-content-end align-items-center my-2">
                  <FacebookShareButton
                    className="ms-2 "
                    url={window.location.href}
                    quote={isPreview ? course.title.value : course.title}
                  >
                    <FacebookIcon size={35} round />
                  </FacebookShareButton>

                  <LinkedinShareButton
                    className="ms-2 "
                    url={window.location.href}
                  >
                    <LinkedinIcon size={35} round />
                  </LinkedinShareButton>

                  <TwitterShareButton
                    className="ms-2 "
                    url={window.location.href}
                    title={isPreview ? course.title.value : course.title}
                  >
                    <TwitterIcon size={35} round />
                  </TwitterShareButton>

                  <WhatsappShareButton
                    className="ms-2 "
                    url={window.location.href}
                    title={isPreview ? course.title.value : course.title}
                    separator=":: "
                  >
                    <WhatsappIcon size={35} round />
                  </WhatsappShareButton>
                </div>
              )}
            </Col>
          </Row>
        </CardHeader>
        <CardBody>
          <Card className="shadow-lg border-0 rounded-4 overflow-hidden">
            <CardBody>
              <Row>
                <Col lg="12" md="12">
                  <div className="publication_description mb-4" id="publication_description">
                    <Card className="mb-2 border-0 bg-light">
                      <CardBody>
                        <h3 className="h6 fw-bold">
                          Descripción
                        </h3>
                        {description}
                      </CardBody>
                    </Card>
                  </div>
                </Col>
              </Row>
              <Row className="mb-4">
                <Col lg="12" md="12">
                  <Card className="mb-2 border-0 bg-light" id="publication_requirements">
                    <CardBody>
                      <h3 className="h6 fw-bold">
                        <i className="fas fa-list-ul me-2"></i>
                        Requisitos Mínimos
                      </h3>
                      <div className="rte-viewer">
                        {isPreview
                          ? safeParse(minisitePublication.requirements.value)
                          : safeParse(minisitePublication.requirements)}
                      </div>
                      <hr />
                      <span className="d-block">
                        <strong>Experiencia:&nbsp;</strong>
                        {isPreview
                          ? minisitePublication.experienceYears.value
                          : minisitePublication.experienceYears}{" "}
                        años.
                      </span>
                      <span className="d-block">
                        <strong>Nivel Educacional:&nbsp;</strong>
                        {minisitePublication.educationLevel.value
                          ? I18n.t(
                            "activerecord.enums.publication/base.education_levels." +
                            minisitePublication.educationLevel.value
                          )
                          : null}
                      </span>
                      {(role === 'admin' || role === 'recruiter') && profilePositionFileUrl && (
                        <div className="mt-3">
                          <strong>Perfil del Cargo (Archivo):</strong>{" "}
                          <a
                            href={profilePositionFileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-link"
                          >
                            <i className="fa fa-file-pdf-o me-1" />
                            Descargar / Ver Archivo
                          </a>
                        </div>
                      )}
                    </CardBody>
                  </Card>

                  <Card className="mb-2 border-0 bg-light" id="publication_benefits">
                    <CardBody>
                      <h3 className="h6 fw-bold">
                        <i className="far fa-smile-wink me-2"></i>
                        Beneficios
                      </h3>
                      {benefits}
                      <br/>
                      <strong>Tipo de Contrato: </strong>
                      {course.contractType.label}
                      <br />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
              <div id="publication_info">
                {isExpired && !isPrivileged ? (
                  <Row>
                    <Col md="12">
                      <Card className="bg-light border-0">
                        <CardBody className="text-center">
                          <h4 className="mb-2">
                            Esta oferta expiró el {moment(desactivateAt).format("DD/MM/YYYY")}.
                          </h4>
                          <small>
                            Ya no admite nuevas postulaciones.
                          </small>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                ) : isPreview ? (
                  <PublicationQuestions
                    publicationQuestions={course.minisitePublication?.publicationQuestions || []}
                  />
                ) : userSignedIn ? (
                  <PostulationForm
                    publicationId={course.minisitePublication.id}
                    publicationQuestions={course.minisitePublication?.publicationQuestions || []}
                    postulationAnswers={postulationAnswers}
                    isAlreadyPostulated={isAlreadyPostulated}
                    user={user}
                    role={role}
                    currentUserRole={currentUserRole}
                  />
                ) : isPrivileged ? (
                  // Admin/Recruiter logueado: puede ver las preguntas pero no postular
                  <PublicationQuestions
                    publicationQuestions={course.minisitePublication?.publicationQuestions || []}
                  />
                ) : (
                  // Usuario no logueado: mostrar mensaje de inicio de sesión
                  (!isExpired) && (
                    <Fragment>
                      <Row>
                        <Col md="12">
                          <Card className="bg-light border-0">
                            <CardBody>
                              <h4 className="text-center">
                                <strong className="fw-bold d-block">
                                  ¿Quieres postular a esta oferta de empleo?
                                </strong>
                                <small>
                                  Para postular a este empleo debes iniciar sesión.
                                  Si aún no tienes cuenta Regístrate.
                                </small>
                              </h4>
                              <div className="text-center">
                                <Button color="primary" href="/users/sign_in" size="sm" className="mx-2 fw-bold">
                                  Inicia Sesión
                                </Button>
                                <Button color="secondary" href="/users/sign_up" size="sm" className="mx-2 fw-bold">
                                  Regístrate
                                </Button>
                              </div>
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>
                    </Fragment>
                  )
                )}
              </div>
            </CardBody>
          </Card>
        </CardBody>
      </Card>
    </div>
  );
};

export default PublicationShow;
